import Layout from "@/components/elements/Layout";
import Link from "next/link";

export default function TOS() {
  return (
    <Layout title={"Terms of Service"}>
      <div className='px-4 mx-auto prose'>
        <h1 className='pt-8 mb-0 text-4xl font-bold leading-tight text-white lg:text-6xl font-display'>
          Terms of Service
        </h1>
        <p className='text-sm text-white lg:text-base text-opacity-80'>
          We’ve updated our Terms of Service to clarify our rights and
          obligations as they relate to Lucidity Insights’ features and
          functionality.
        </p>
        <div className='max-w-screen-xl pt-4 lg:py-8'>
          <article className='prose prose-invert pb-8 prose-img:rounded-[10px] prose-td:p-2 max-w-5xl'>
            <p>
              <strong>
                PLEASE NOTE THAT YOUR USE OF AND ACCESS TO OUR SERVICE (DEFINED
                BELOW) IS SUBJECT TO THE FOLLOWING TERMS; IF YOU DO NOT AGREE TO
                ALL OF THE FOLLOWING, YOU MAY NOT USE OR ACCESS THE SERVICE IN
                ANY MANNER.
              </strong>
            </p>
            <p>Last Updated: July 1st, 2022</p>
            <p>
              Welcome to Lucidity Insights. Please read on to learn the rules
              and restrictions that govern your use of our websites, products,
              services and applications (the “Service”). If you have any
              questions, comments, or concerns regarding these terms or the
              Service, please contact us at{" "}
              <a href='mailto:support@lucidityinsights.com'>
                support@lucidityinsights.com
              </a>
            </p>
            <p>
              <strong>
                These Terms of Service (the “Terms”) are a binding contract
                between you and Lucidity Insights FZ LLC (“Lucidity Insights,”
                “we”, “our” and “us”). By using the Service in any way, you
                agree to and accept all of these Terms, and these Terms will
                remain in effect at all times while you use the Service or any
                data obtained through the Service. These Terms govern your
                access to the Service and use of all data, content and other
                information on Lucidity Insight’s websites, applications,
                products and other properties and include the provisions in this
                document.
              </strong>
            </p>
            <p>
              <strong>
                ARBITRATION NOTICE AND CLASS ACTION WAIVER: EXCEPT FOR CERTAIN
                TYPES OF DISPUTES DESCRIBED IN THE ARBITRATION SECTION BELOW,
                YOU AGREE THAT DISPUTES BETWEEN YOU AND US WILL BE RESOLVED BY
                BINDING, INDIVIDUAL ARBITRATION AND YOU WAIVE YOUR RIGHT TO
                PARTICIPATE IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.
              </strong>
            </p>
            <h3>Will These Terms Ever Change?</h3>
            <p>
              We are constantly trying to improve our Service, so these Terms
              may need to change along with the Service. We reserve the right to
              change these Terms at any time, but if we do, we will bring it to
              your attention by placing a notice on the Lucidity Insights
              Website, by sending you an email, and/or by some other means.
            </p>
            <p>
              If you don’t agree with the new Terms, you are free to reject
              them; unfortunately, that means you will no longer be able to use
              the Service. If you use the Service in any way after a change to
              these Terms is effective, that means you agree to all of the
              changes.
            </p>
            <p>
              Except for changes by us as described here, no other amendment or
              modification of these Terms will be effective unless in writing
              and signed by you and Lucidity Insights.
            </p>
            <h3>What About My Privacy?</h3>
            <p>
              Lucidity Insights takes the privacy of its users very seriously.
               Check out Lucidity Insights current{" "}
              <Link legacyBehavior passHref href={"/privacy-policy"}>
                <a>Privacy Policy here</a>
              </Link>
              .
            </p>
            <p>
              The Children’s Online Privacy Protection Act (“COPPA”) requires
              that online service providers obtain parental consent before they
              knowingly collect personally identifiable information online from
              children. We do not knowingly collect or solicit personally
              identifiable information from children under 16; if you are a
              child under 16, please do not attempt to register for the Service
              or send any personal information about yourself to us. If we learn
              we have collected personal information from a child under 16, we
              will delete that information as quickly as possible. If you
              believe that a child under 16 may have provided us personal
              information, please contact us at{" "}
              <a href='mailto:support@lucidityinsights.com'>
                support@lucidityinsights.com
              </a>
              .
            </p>
            <h3>What are the basics of using the Service?</h3>
            <p>
              As part of using the Service you may be required to sign up for an
              account and select a password and email or user name (your
              “Lucidity Insights User ID”).  You promise to provide us with
              accurate, complete, and updated registration information about
              yourself.  You may not select as your Lucidity Insights User ID a
              name that you don’t have the right to use, or another person’s
              name with the intent to impersonate that person. Your right to
              access the Service is personal to you and you may not transfer
              your account to anyone else without our prior written permission.
              You will not share your account or password with anyone, and you
              must protect the security of your account and your password.
               You’re responsible for any activity associated with your account.
              You will only use the Service and Content (defined below) in a
              manner that complies with all laws that apply to you. If your use
              of the Service is prohibited by applicable laws, then you aren’t
              authorized to use the Service. We can’t and won’t be responsible
              for your use of the Service in any way that breaks the law.
            </p>
            <p>
              You represent and warrant that you are of legal age to form a
              binding contract (or if not, you’ve received your parent’s or
              guardian’s permission to use the Service and gotten your parent or
              guardian to agree to these Terms on your behalf). If you’re
              agreeing to these Terms on behalf of an organization or entity,
              you represent and warrant that you are authorized to agree to
              these Terms on that organization or entity’s behalf and bind them
              to these Terms (in which case, the references to “you” and “your”
              in these Terms, except for in this sentence, refer to that
              organization or entity).
            </p>
            <h3>
              Your use of the Service is subject to the following additional
              restrictions:
            </h3>
            <p>
              You represent, warrant, and agree that you will not contribute or
              submit any User Submission (defined below) or other materials or
              otherwise use the Service or interact with the Service in a manner
              that:
              <ol type='a'>
                <li>
                  Infringes or violates the intellectual property rights or any
                  other rights of anyone else (including Lucidity Insights);
                </li>
                <li>
                  Violates any law or regulation, including without limitation
                  any applicable export control laws and/or data privacy laws,
                  or would cause Lucidity Insights to be in violation of any law
                  or regulation (including, without limitation, any laws
                  relating to labor, employment, or the issuance of securities);
                </li>
                <li>
                  Is harmful, fraudulent, deceptive, threatening, harassing,
                  defamatory, obscene, or otherwise objectionable;
                </li>
                <li>
                  Jeopardizes the security of your Lucidity Insights account or
                  anyone else’s (such as allowing someone else to log in to the
                  Service as you);
                </li>
                <li>
                  Attempts, in any manner, to obtain the password, account, or
                  other security information from any other user;
                </li>
                <li>
                  Violates the security of any computer network, or cracks any
                  passwords or security encryption codes;
                </li>
                <li>
                  Runs any form of auto-responder or “spam” on the Service, or
                  any processes that run or are activated while you are not
                  logged into the Service, or that otherwise interfere with the
                  proper working of the Service (including by placing an
                  unreasonable load on the Service infrastructure);
                </li>
                <li>
                  “Crawls,” “scrapes,” or “spiders” any page, data, or portion
                  of or relating to the Service or Content (through use of
                  manual or automated means);
                </li>
                <li>
                  Circumvents or attempts to circumvent any features,
                  limitations, or restrictions of the Service (including,
                  without limitation, attempting to access, download, export, or
                  otherwise use or exploit any Content using any automated means
                  or tools);
                </li>
                <li>
                  Copies or stores any significant portion of the Content;
                </li>
                <li>
                  Decompiles, reverse engineers, or otherwise attempts to obtain
                  the source code or underlying ideas or information of or
                  relating to the Service.
                </li>
              </ol>
            </p>
            <p>
              A violation of any of the foregoing is grounds for account
              suspension or termination of your right to use or access the
              Service.
            </p>
            <h3>What are my rights with respect to this Service?</h3>
            <p>
              The materials displayed or published or available on or through
              the Service, including, but not limited to, text, graphics, data,
              articles, information, photos, images, illustrations, User
              Submissions, and so forth (all of the foregoing, the “Content”)
              are protected by copyright and/or other intellectual property
              laws. You promise to abide by all copyright notices, trademark
              rules, information, and restrictions contained in any Content you
              access through the Service in any way (including through the
              Lucidity Insights website, API, mobile applications, downloads,
              etc.). You will not use, copy, reproduce, adapt, modify,
              translate, publish, broadcast, transmit, distribute, perform,
              upload, display, license, sell or otherwise exploit for any
              purpose any Content not owned by you in a way that violates
              someone else’s (including Lucidity Insights’s) rights.
               Notwithstanding the foregoing, you may display insubstantial
              excerpts of Content for criticism, commentary, news reporting,
              teaching, scholarship, research and similar purposes, provided:
              <ul>
                <li>The use does not compete with the Services;</li>
                <li>
                  And Proper attribution is provided to Lucidity Insights
                  (please follow{" "}
                  <Link legacyBehavior passHref href={"/attribution-instructions"}>
                    <a>out Attribution Instructions</a>
                  </Link>
                  , to ensure that you are properly attributing Lucidity
                  Insights)
                </li>
              </ul>
            </p>
            <p>
              Any other uses of Content require Lucidity Insights’s prior
              written consent.
            </p>
            <p>
              You understand that Lucidity Insights owns the Service. You won’t
              modify, publish, transmit, participate in the transfer or sale of,
              reproduce (except as expressly provided in this Section), create
              derivative works based on, re-sell, sublicense, re-distribute, or
              otherwise exploit any aspect of the Service.
            </p>
            <p>
              If you would like to use Content in any manner other than as
              expressly permitted by these Terms (for example, exporting data
              via Lucidity Insights’s API or including data in any other product
              or service), please visit our products page to learn more about
              the different ways that Lucidity Insights provides its data and
              find the solution that is right for you. You may be required to
              submit an application and/or meet certain eligibility criteria in
              order to participate in a program, and there may be different
              terms and conditions which apply to the specific program that you
              choose. You must agree to all terms and conditions and, if
              applicable, pay all required fees which apply to your specific
              program. These Terms apply to all such programs unless
              specifically stated otherwise in the applicable program terms.
            </p>
            <h3>
              Do I have to grant any licenses to Lucidity Insights or to Other
              Users?
            </h3>
            <p>
              Certain parts of the Service may allow you to integrate with other
              third-party accounts of yours, such as data from your productivity
              tools (e.g. CRMs, e-mail, sales engagement tools, etc.). If you
              choose to use these parts of the Service, you grant Lucidity
              Insights all licenses and rights necessary for Lucidity Insights
              to provide the Service to you, including a license for Lucidity
              Insights to read and write to your third-party account, to extract
              data from and upload data to your third-party account, and to
              share data extracted from your third-party account with other
              members of your team or institution, as further described in the
              next paragraph.
            </p>
            <p>
              Anything you post, upload, share, store, integrate with or
              otherwise provide through the Service is your “User Submission.”
              Some User Submissions (such as profile data you contribute to
              Lucidity Insights, or information you elect to share with other
              users through the Service, such as your team members) are viewable
              by other users.  For all User Submissions, you hereby grant
              Lucidity Insights a royalty-free, perpetual, sublicenseable,
              irrevocable, and worldwide license to translate, modify,
              aggregate, reproduce, display, distribute, and otherwise use and
              exploit such User Submissions for Lucidity Insights’s business
              purposes, including to provide, develop, and improve our products
              and services, except, for the avoidance of doubt, the foregoing
              shall not permit Lucidity Insights to publish, display or
              distribute your data from your productivity tools (e.g. CRMs,
              e-mail, sales engagement tools, etc.) in a manner that is
              identifiable as originating from you without your written
              consent.  Please note that the foregoing license is subject to our{" "}
              <Link legacyBehavior passHref href={"/privacy-policy"}>
                <a>Privacy Policy </a>
              </Link>{" "}
              to the extent it relates to User Submissions that are also your
              personally-identifiable information, and to our Data Use Addendum
              to the extent it relates to User Submissions that are the
              personally-identifiable information of another person. You are
              prohibited from including Social Security Numbers, driver’s
              license numbers, passport numbers, financial account and payment
              card information (except where expressly requested by Lucidity
              Insights), precise geolocation, and health and biometric
              information as part of any User Submission.
            </p>
            <h3>
              What if I see something on the Service that infringes my
              copyright?
            </h3>
            <p>
              Under the WIPO Copyright Treaty, the European Union Copyright
              Directive (the “EUCD”, and the Digital Millennium Copyright Act
              (the “DMCA”), online service providers, like Lucidity Insights,
              may be asked to remove material that allegedly violates someone’s
              copyright. We respect others’ intellectual property rights, and we
              reserve the right to delete or disable Content alleged to be
              infringing, and to terminate the accounts of repeat alleged
              infringers. If you believe someone’s copyrights have been
              violated, you can contact us at{" "}
              <a href='mailto:support@lucidityinsights.com'>
                support@lucidityinsights.com
              </a>
            </p>
            <h3>Who is responsible for what I see and do on the Service?</h3>
            <p>
              Any information or Content publicly posted or privately
              transmitted through the Service is the sole responsibility of the
              person or organization from whom such Content originated. You
              access all such information and Content at your own risk, and we
              aren’t liable for any errors or omissions in that information or
              Content or for any damages or loss you might suffer in connection
              with it. We cannot control and have no duty to take any action
              regarding how you may interpret and use the Content or what
              actions you may take as a result of having been exposed to the
              Content, and you hereby release us from all liability for you
              having acquired or not acquired Content through the Service. We
              can’t guarantee the identity of any users with whom you interact
              when using the Service and are not responsible for which users
              gain access to the Service.
            </p>
            <p>
              You are responsible for all Content you contribute, in any manner,
              to the Service, and you represent and warrant you have all rights
              necessary to do so, in the manner in which you contribute
              it. Content includes, without limitation, personally identifiable
              information about you or any other person. You will keep all your
              registration information accurate and current. You are responsible
              for all your activity in connection with the Service.
            </p>
            <p>
              Content that constitutes personally identifiable information may
              be subject to privacy laws or regulations. You are solely
              responsible for understanding how privacy laws and regulations may
              impact your use of such Content and for ensuring that your use
              (including retention, storage, and all other forms of processing)
              complies with all such laws and regulations.
            </p>
            <p>
              The Service may contain content, data, links or connections to or
              from third-party websites or services that are not owned or
              controlled by Lucidity Insights (“Third-Party Service(s)”). When
              you access or use Third-Party Services, you accept that there are
              risks in doing so, and that Lucidity Insights is not responsible
              for such risks.  We encourage you to be aware when you leave the
              Service and to read the terms and conditions and privacy policy of
              each Third-Party Service that you visit or utilize.
            </p>
            <p>
              Lucidity Insights has no control over, and assumes no
              responsibility for, the content, accuracy, privacy policies, or
              practices of or opinions expressed in any Third-Party Services or
              by any third-party that you interact with through the Service. In
              addition, Lucidity Insights will not and cannot monitor, verify,
              censor or edit the content of any Third-Party Services. By using
              the Service, you release and hold us harmless from any and all
              liability arising from your access to or use of any Third-Party
              Service.
            </p>
            <p>
              Your interactions with organizations and/or individuals found on
              or through the Service, including payment and delivery of goods or
              services (if applicable), and any other terms, conditions,
              warranties or representations associated with such dealings, are
              solely between you and such organizations and/or individuals. You
              should make whatever investigation you feel necessary or
              appropriate before proceeding with any online or offline
              transaction with any of these third-parties. You agree that
              Lucidity Insights shall not be responsible or liable for any loss
              or damage of any sort incurred as the result of any such dealings.
            </p>
            <p>
              If there is a dispute between participants on this site, or
              between users and any third- party, you agree that Lucidity
              Insights is under no obligation to become involved. In the event
              that you have a dispute with one or more other users, you release
              Lucidity Insights, its affiliates, and its and their directors,
              officers, agents, employees, partners, and successors from claims,
              demands, and damages of every kind or nature, known or unknown,
              suspected or unsuspected, disclosed or undisclosed, arising out of
              or in any way related to such disputes and/or our Service.
            </p>
            <h3>Will Lucidity Insights Ever Change the Service?</h3>
            <p>
              We’re always trying to improve the Service, so it may change over
              time. We may suspend or discontinue any part of the Service, or we
              may introduce new features or impose limits on certain features or
              restrict access to parts or all of the Service. We’ll try to give
              you notice when we make a material change to the Service that
              would adversely affect you, but this isn’t always practical.
              Similarly, we reserve the right to remove any Content from the
              Service at any time, for any reason (including, but not limited
              to, if someone alleges that Content was contributed in violation
              of these Terms), in our sole discretion, and without notice.
            </p>
            <p>
              From time to time, Lucidity Insights may require you to expunge
              some or all of the Content in your possession, and you will do so
              promptly upon request.
            </p>
            <h3>Does Using Lucidity Insights cost Anything?</h3>
            <p>
              We reserve the right to charge for certain parts or all of the
              Service. Certain aspects or uses of the Service require you to pay
              fees, for example, in order to access certain content or be able
              to download or export data. Visit our Products Page for current
              descriptions of these services and the applicable fees. You agree
              to pay all fees which apply to your use of the Service. Lucidity
              Insights reserves the right to change any fees and pricing
              applicable to the Service at any time upon notice to you (which
              may be sent by email or posted on Lucidity Insights’s website).
              Your use of the Service following such notification constitutes
              your acceptance of any new or increased fees.
            </p>
            <h3>What if I want to stop using Lucidity Insights?</h3>
            <p>
              You’re free to do that at any time; please refer to our 
              <Link legacyBehavior passHref href='/terms-of-service#cancellation-policy'>
                Cancellation Policy
              </Link>{" "}
              and{" "}
              <Link legacyBehavior passHref href={"/privacy-policy"}>
                <a>Privacy Policy</a>
              </Link>
              , as well as the licenses above, to understand how we treat
              information you provide to us after you have stopped using our
              Service.
            </p>
            <p>
              Lucidity Insights is also free to terminate (or suspend access to)
              your use of the Service or your account, for any reason in our
              discretion, including your breach of these Terms. Lucidity
              Insights has the sole right to decide whether you are in violation
              of any of the restrictions set forth in these Terms.
            </p>
            <p>
              Account termination may result in destruction of any Content
              associated with your account, so keep that in mind before you
              decide to terminate your account. Provisions that, by their
              nature, should survive termination of these Terms shall survive
              termination. By way of example, all of the following will survive
              termination: any obligation you have to pay us or indemnify us,
              any limitations on our liability, any terms regarding ownership or
              intellectual property rights, and terms regarding disputes between
              us.
            </p>
            <p>
              Additionally, upon termination of your account Lucidity Insights
              may require you to expunge some or all of the Content in your
              possession, and you will do so promptly.
            </p>
            <h3>What Else Do I need to Know?</h3>
            <p>
              <i>Warranty Disclaimer.</i> Neither Lucidity Insights nor its
              licensors or suppliers makes any representations or warranties
              concerning the Service or any Content contained in or accessed
              through the Service, and we will not be responsible or liable for
              the accuracy, copyright compliance, legality, or decency of any
              materials (including Content) contained in or accessed through the
              Service. We (and our licensors and suppliers) make no
              representations or warranties regarding suggestions or
              recommendations of services or products offered or purchased
              through the Service. Products and services purchased or offered
              (whether or not following such recommendations and suggestions)
              through the Service are provided “AS IS” and without any warranty
              of any kind from Lucidity Insights or others (unless, with respect
              to such others only, provided expressly and unambiguously in
              writing by a designated third-party for a specific product).  THE
              SERVICE AND CONTENT ARE PROVIDED BY LUCIDITY INSIGHTS (AND ITS
              LICENSORS AND SUPPLIERS) ON AN “AS-IS” BASIS, WITHOUT WARRANTIES
              OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, WITHOUT
              LIMITATION, IMPLIED WARRANTIES OF MERCHANTABILITY, ACCURACY,
              AVAILABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT,
              OR THAT USE OF THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE.
              SOME STATES DO NOT ALLOW LIMITATIONS ON HOW LONG AN IMPLIED
              WARRANTY LASTS, SO THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU.
            </p>
            <p>
              <i>Limitation of Liability.</i> TO THE FULLEST EXTENT ALLOWED BY
              APPLICABLE LAW, UNDER NO CIRCUMSTANCES AND UNDER NO LEGAL THEORY
              (INCLUDING, WITHOUT LIMITATION, TORT, NEGLIGENCE, CONTRACT, STRICT
              LIABILITY, OR OTHERWISE) SHALL LUCIDITY INSIGHTS (OR ITS LICENSORS
              OR SUPPLIERS) BE LIABLE TO YOU OR TO ANY OTHER PERSON FOR (A) ANY
              INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES OF ANY
              KIND, INCLUDING DAMAGES FOR LOST PROFITS, LOSS OF GOODWILL, WORK
              STOPPAGE, ACCURACY OF RESULTS, COST OF PROCUREMENT OF SUBSTITUTE
              GOODS OR SERVICES, OR COMPUTER FAILURE OR MALFUNCTION, OR (B) ANY
              AMOUNT, IN THE AGGREGATE, IN EXCESS OF THE GREATER OF (I) $100 OR
              (II) THE AMOUNTS PAID BY YOU TO LUCIDITY INSIGHTS IN CONNECTION
              WITH THE SERVICE IN THE TWELVE (12) MONTH PERIOD PRECEDING THE
              APPLICABLE CLAIM, OR (C) ANY MATTER BEYOND OUR REASONABLE CONTROL.
              SOME STATES DO NOT ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN
              DAMAGES, SO THE ABOVE LIMITATION AND EXCLUSIONS MAY NOT APPLY TO
              YOU.
            </p>
            <p>
              <i>Indemnity.</i> You agree to indemnify and hold Lucidity
              Insights, its affiliates, and its and their directors, officers,
              agents, employees, partners, and successors harmless from and
              against any and all claims, liabilities, penalties (including
              governmental fines), damages (actual and consequential), losses
              and expenses (including attorneys’ fees) arising from or in any
              way relating to (a) your use of the Service (including any actions
              taken by a third-party using your account), or (b) your violation
              of these Terms.
            </p>
            <p>
              <i>Assignment.</i> You may not assign, delegate or transfer these
              Terms or your rights or obligations hereunder, or your Service
              account, in any way (by operation of law or otherwise) without
              Lucidity Insights’s prior written consent. We may transfer,
              assign, or delegate these Terms and our rights and obligations
              without consent.
            </p>
            <p>
              <i>Choice of Law; Arbitration.</i> These Terms are governed by and
              will be construed under the laws of the United Arab Emirates,
              without regard to the conflicts of laws provisions thereof. Any
              dispute arising from or relating to the subject matter of these
              Terms (whether brought against Lucidity Insights or against any
              director, officer or employee of Lucidity Insights in their
              personal capacity) shall be finally settled in Dubai, United Arab
              Emirates, in English and Arabic, in accordance with the rules and
              laws of the United Arab Emirates. Judgment upon the award rendered
              by such arbitrator may be entered in any court of competent
              jurisdiction. Notwithstanding the foregoing obligation to
              arbitrate disputes, each party shall have the right to pursue
              injunctive or other equitable relief at any time, from any court
              of competent jurisdiction. For all purposes of these Terms, the
              parties consent to exclusive jurisdiction and venue in the emirate
              or federal courts located in, respectively, Dubai, United Arab
              Emirates or Abu Dhabi, United Arab Emirates. 
              <strong>
                Any arbitration under these Terms will take place on an
                individual basis: class arbitrations and class actions are not
                permitted. YOU UNDERSTAND AND AGREE THAT BY ENTERING INTO THESE
                TERMS, YOU AND LUCIDITY INSIGHTS ARE EACH WAIVING THE RIGHT TO
                TRIAL BY JURY OR TO PARTICIPATE IN A CLASS ACTION.
              </strong>
            </p>
            <p>
              <i>Third-Party Trademarks.</i> All third-party names, logos,
              product and service names, designs, and slogans (collectively,
              “Third-Party Trademark(s)”) contained in the Service are the
              property of their respective owners. Third-Party Trademarks are
              used by Lucidity Insights to refer to the owners of the
              Third-Party Trademark(s) in question and use by Lucidity Insights
              is not intended to (and does not) constitute or imply any kind of
              relationship between Lucidity Insights and the owners of the
              Third-Party Trademark(s) in question, including any affiliation,
              sponsorship, endorsement or approval of products and/or services,
              unless otherwise expressly indicated.
            </p>
            <p>
              <i>Safe Harbor Statement.</i> Any statements made by Lucidity
              Insights, oral or written, about new Service features or
              functionality are intended to outline Lucidity Insights’s general
              product direction for information purposes only and are not a
              commitment to deliver any material, applications, or
              functionality, and should not be relied upon. The development,
              release, and timing of any features or functionality remains at
              Lucidity Insights’s sole discretion.
            </p>
            <p>
              <i>Miscellaneous.</i> You will be responsible for paying,
              withholding, filing, and reporting all taxes, duties, and other
              governmental assessments associated with your activity in
              connection with the Service, provided that Lucidity Insights may,
              in its sole discretion, do any of the foregoing on your behalf or
              for itself as it sees fit. The failure of either you or us to
              exercise, in any way, any right herein shall not be deemed a
              waiver of any further rights hereunder. If any provision of these
              Terms is found to be unenforceable or invalid, that provision will
              be limited or eliminated, to the minimum extent necessary, so that
              these Terms shall otherwise remain in full force and effect and
              enforceable. You and Lucidity Insights agree that these Terms are
              the complete and exclusive statement of the mutual understanding
              between you and Lucidity Insights, and that they supersede and
              cancel all previous written and oral agreements, communications
              and other understandings relating to the subject matter of these
              Terms. You hereby acknowledge and agree that you are not an
              employee, agent, partner, or joint venture of Lucidity Insights,
              and you do not have any authority of any kind to bind Lucidity
              Insights in any respect whatsoever. You and Lucidity Insights
              agree there are no third-party beneficiaries intended under these
              Terms.
            </p>
            <h3 id='cancellation-policy'>Cancellation Policy</h3>
            <p>
              We do not provide subscription refunds for Lucidity Insights
              products. But, if you are unsatisfied with our products, you can
              terminate any future subscriptions for future months or years.
            </p>
            <p>
              If, and only if, you have purchased an annual package within the
              past 7 days, and have not accessed any locked content, you can
              send a special cancellation request to
              billing@lucidityinsights.com within 7 days of your subscription
              beginning and we will review on a case by case basis, to help you
              terminate your subscription early and get a partial refund. If any
              locked content was accessed, no refunds will be issued.
            </p>
            <p>
              If you would like to cancel your subscription, go to ‘My Account’
              and select ‘My Plan’ under ‘Subscriptions’. You can “Stop
              Subscription” there. This will cancel only future charges
              associated with your subscription. Cancellations are effective at
              the end of the current billing period, and as such, Lucidity
              Insights does not offer prorated refunds for cancelled
              subscriptions. All subscriptions are recurring and will
              automatically renew after the end of each paid subscription
              period. You will not receive a refund for the current billing
              cycle as you will continue to have the same access and benefits of
              your subscription for the remainder of the current billing period.
            </p>
            <p>
              Lucidity Insights is also free to terminate (or suspend access to)
              your use of your subscription or your account, for any reason in
              our discretion, including a breach of our{" "}
              <Link legacyBehavior href='/terms-of-service' passHref>
                <a>Terms of Service</a>
              </Link>
              . Lucidity Insights has the sole right to decide whether you are
              in violation of any of the restrictions set forth in these Terms.
              You will not be entitled to a refund in these circumstances.
            </p>
            <p>
              We reserve the right to issue refunds or credits at our sole
              discretion. If we issue a refund or credit, we are under no
              obligation to issue the same or similar refund in the future.
            </p>
          </article>
        </div>
      </div>
    </Layout>
  );
}
